import { QUERY_OPERATORS } from "./operators";
import type { QueryOperator } from "./operators";
import type { Filters, QueryBuilder } from "./QueryBuilder";
import { QueryParamEntry, QueryParamRecord } from "./QueryBuilder";

export const MULTI_VALUE_OPERATORS = {
  [QUERY_OPERATORS.inc]: true,
  [QUERY_OPERATORS.exc]: true,
  [QUERY_OPERATORS.eq]: true,
  [QUERY_OPERATORS.neq]: true,
} as const;

export type MultiValueOperator = keyof typeof MULTI_VALUE_OPERATORS;

export const SINGLE_VALUE_OPERATORS = {
  [QUERY_OPERATORS.lt]: true,
  [QUERY_OPERATORS.gt]: true,
  [QUERY_OPERATORS.lte]: true,
  [QUERY_OPERATORS.gte]: true,
} as const;

export type SingleValueOperator = keyof typeof SINGLE_VALUE_OPERATORS;

export const FILTER_COMBINATORS = {
  OR: "OR",
  AND: "AND",
} as const;

export type FilterCombinator =
  (typeof FILTER_COMBINATORS)[keyof typeof FILTER_COMBINATORS];

type FilterOptions =
  | {
      field: string;
      operator: MultiValueOperator;
      value: unknown;
      combinator?: FilterCombinator;
    }
  | {
      field: string;
      operator: SingleValueOperator;
      value: Exclude<unknown, unknown[]>;
    }
  | {
      field: string;
      operator: "empty" | "nempty";
      value?: undefined;
    };

export class DefaultQueryBuilder implements QueryBuilder {
  #filters: Filters = {};

  static toSearchParams(entries: Iterable<QueryParamEntry>): URLSearchParams {
    const params = new URLSearchParams();

    for (const [key, value] of entries) {
      params.append(key, value);
    }

    return params;
  }

  static toRecord(entries: Iterable<QueryParamEntry>): QueryParamRecord {
    const query: QueryParamRecord = {};

    for (const [key, value] of entries) {
      query[key] = value;
    }

    return query;
  }

  /**
   * Adds a filter with given operator and value(s).
   * If value is an array, combines with OR (default) or AND operator.
   * @example
   * // Single value
   * .addFilter({ field: 'status', operator: 'eq', value: 'active' })
   * // Multiple values with OR (default)
   * .addFilter({ field: 'status', operator: 'eq', value: ['active', 'pending'] })
   * // Multiple values with AND
   * .addFilter({ field: 'role', operator: 'eq', value: ['admin', 'user'], combinator: 'AND' })
   */
  addFilter(options: FilterOptions): this {
    const { field, operator, value } = options;

    if (!this.#filters[field]) {
      this.#filters[field] = {};
    }

    if (Array.isArray(value)) {
      const combinator =
        "combinator" in options
          ? options.combinator ?? FILTER_COMBINATORS.OR
          : FILTER_COMBINATORS.OR;
      const combined = value.map((v) => String(v)).join(` ${combinator} `);
      this.#filters[field][operator] = combined;
    } else {
      this.#filters[field][operator] = value;
    }

    return this;
  }

  /**
   * Convenience method: adds an includes filter
   * Supports both single and multiple values with OR/AND combinator.
   * @example
   * .addFilterInc({ field: 'tags', value: 'important' })
   * .addFilterInc({ field: 'tags', value: ['important', 'urgent'] })
   * .addFilterInc({ field: 'tags', value: ['lang:en', 'lang:pl'], combinator: 'AND' })
   */
  addFilterInc(options: {
    field: string;
    value: unknown;
    combinator?: FilterCombinator;
  }): this {
    return this.addFilter({
      ...options,
      operator: QUERY_OPERATORS.inc,
    });
  }

  addFilterExc(options: {
    field: string;
    value: unknown;
    combinator?: FilterCombinator;
  }): this {
    return this.addFilter({
      ...options,
      operator: QUERY_OPERATORS.exc,
    });
  }

  addFilterEq(options: {
    field: string;
    value: unknown;
    combinator?: FilterCombinator;
  }): this {
    return this.addFilter({
      ...options,
      operator: QUERY_OPERATORS.eq,
    });
  }

  addFilterNeq(options: {
    field: string;
    value: unknown;
    combinator?: FilterCombinator;
  }): this {
    return this.addFilter({
      ...options,
      operator: QUERY_OPERATORS.neq,
    });
  }

  addFilterLt(options: {
    field: string;
    value: Exclude<unknown, unknown[]>;
  }): this {
    return this.addFilter({
      ...options,
      operator: QUERY_OPERATORS.lt,
    });
  }

  addFilterGt(options: {
    field: string;
    value: Exclude<unknown, unknown[]>;
  }): this {
    return this.addFilter({
      ...options,
      operator: QUERY_OPERATORS.gt,
    });
  }

  addFilterLte(options: {
    field: string;
    value: Exclude<unknown, unknown[]>;
  }): this {
    return this.addFilter({
      ...options,
      operator: QUERY_OPERATORS.lte,
    });
  }

  addFilterGte(options: {
    field: string;
    value: Exclude<unknown, unknown[]>;
  }): this {
    return this.addFilter({
      ...options,
      operator: QUERY_OPERATORS.gte,
    });
  }

  addFilterEmpty(field: string): this {
    return this.addFilter({
      field,
      operator: QUERY_OPERATORS.empty,
      value: undefined,
    });
  }

  addFilterNotEmpty(field: string): this {
    return this.addFilter({
      field,
      operator: QUERY_OPERATORS.nempty,
      value: undefined,
    });
  }

  buildEntries(): QueryParamEntry[] {
    const entries: QueryParamEntry[] = [];

    for (const [field, ops] of Object.entries(this.#filters)) {
      for (const [op, value] of Object.entries(ops)) {
        const key = `${field}[${op}]`;
        entries.push([key, String(value)]);
      }
    }

    return entries;
  }

  buildSearchParams(): URLSearchParams {
    return DefaultQueryBuilder.toSearchParams(this.buildEntries());
  }

  /**
   * Initializes this builder from a plain filters object.
   * Existing internal filters are replaced.
   */
  withFilters(filters: Filters | Record<string, unknown>): this {
    this.#filters = {};

    for (const [field, rawValue] of Object.entries(filters)) {
      if (
        rawValue !== null &&
        typeof rawValue === "object" &&
        !Array.isArray(rawValue)
      ) {
        for (const [operator, value] of Object.entries(rawValue)) {
          this.addFilter({
            field,
            operator: operator as QueryOperator,
            value,
          });
        }
        continue;
      }

      // Handle direct field values
      this.addFilterEq({ field, value: rawValue });
    }

    return this;
  }

  build(): QueryParamRecord {
    return DefaultQueryBuilder.toRecord(this.buildEntries());
  }
}
